import { vnDateOf, vnMinuteOfDay } from './attendance';
import { hhmmToMin } from './driver-rules';

/**
 * Màn tài xế theo ngày (anh Tiến 24/10/2026, docs/phases/P11.md §13.12):
 *  - Tab Giao hàng chỉ hiện đơn giao TRONG NGÀY; từ giờ "gắn đơn ngày mai" (mặc định 16:00) gắn thêm đơn ngày mai.
 *  - Đơn đã qua ngày ẩn khỏi tab, vào mục "Đơn tháng này": Final (đủ ảnh + ký nhận) / Fail (thiếu ảnh → tài xế bổ sung).
 *  - Đầu giờ gửi tài xế "Đơn hàng hôm nay gồm: …" + lời nhắc; chạy xuyên trưa đúng quy định → lời cảm ơn trong khung 13:00–14:00.
 */

const addDays = (d: string, n: number) => {
  const x = new Date(`${d}T00:00:00Z`);
  x.setUTCDate(x.getUTCDate() + n);
  return x.toISOString().slice(0, 10);
};

/** Ngày giao hiện ở tab Giao hàng lúc `now`: hôm nay, và từ `nextDayAt` thêm ngày mai. */
export function driverListDates(now: Date, nextDayAt: string): { today: string; dates: string[]; withTomorrow: boolean } {
  const today = vnDateOf(now);
  const withTomorrow = vnMinuteOfDay(now.toISOString()) >= hhmmToMin(nextDayAt);
  return { today, dates: withTomorrow ? [today, addDays(today, 1)] : [today], withTomorrow };
}

/** Tài xế bổ sung ảnh cho phiếu đã giao từ ngày này (01 tháng trước): Đơn tháng này + tháng trước. */
export function driverFixFrom(today: string): string {
  const [y, m] = today.split('-').map(Number) as [number, number];
  return m === 1 ? `${y - 1}-12-01` : `${y}-${String(m - 1).padStart(2, '0')}-01`;
}

/** `now` có nằm trong khung [from, until) giờ VN không. */
export function inVnWindow(now: Date, from: string, until: string): boolean {
  const m = vnMinuteOfDay(now.toISOString());
  return m >= hhmmToMin(from) && m < hhmmToMin(until);
}

export const DRIVER_MONTH_STATES = ['final', 'fail', 'not_delivered', 'failed'] as const;
export type DriverMonthState = (typeof DRIVER_MONTH_STATES)[number];
export const DRIVER_MONTH_STATE_LABELS: Record<DriverMonthState, string> = {
  final: 'Final',
  fail: 'Fail',
  not_delivered: 'Quá ngày chưa giao',
  failed: 'Giao không thành',
};

/**
 * Trạng thái cuối của đơn đã qua ngày. Final = đủ như lúc tài xế bấm Giao xong (≥ 1 ảnh giao + chữ ký trên màn hoặc ảnh phiếu ký nhận);
 * Fail = đã giao mà thiếu (thường do văn phòng xác nhận thay) → tài xế chụp bổ sung là thành Final.
 */
export function driverMonthState(n: { status: string; podPhotos: number; docPhotos: number; hasSignature: boolean }): { state: DriverMonthState; missing: string[] } {
  if (n.status === 'failed') return { state: 'failed', missing: [] };
  if (n.status !== 'delivered') return { state: 'not_delivered', missing: [] };
  const missing: string[] = [];
  if (n.podPhotos < 1) missing.push('ảnh hàng tại chỗ giao');
  if (!n.hasSignature && n.docPhotos < 1) missing.push('chữ ký hoặc ảnh phiếu ký nhận');
  return { state: missing.length ? 'fail' : 'final', missing };
}

/** Tin đầu giờ: "Đơn hàng hôm nay gồm: N đơn" + từng đơn + lời nhắc. */
export function morningBriefMessage(notes: { code: string; customerName: string }[], tail: string): { title: string; message: string } {
  const title = `Đơn hàng hôm nay gồm: ${notes.length} đơn`;
  const lines = notes.map((n, i) => `${i + 1}. ${n.code} · ${n.customerName}`);
  return { title, message: [...lines, tail.trim()].filter(Boolean).join('\n') };
}
